<template>
    <label :class="[$style['mds-radio-button'], classObject, sizeClass]" :data-mds-version="version">
        <input
            v-bind="$attrs"
            :name="name"
            :value="value"
            type="radio"
            :class="$style['mds-radio-button__input']"
            :checked="isChecked"
            :disabled="disabled"
            @change="toggleCheck($event)"
        />
        <span :class="$style['mds-radio-button__visible-wrap']">
            <span :class="$style['mds-radio-button__visual']"></span>
            <span :class="$style['mds-radio-button__text']">
                <slot>{{ label }}</slot>
                <span v-if="microcopy" :class="$style['mds-radio-button__microcopy']" v-html="microcopy"></span>
            </span>
        </span>
    </label>
</template>

<script>
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsRadioButton',
        inheritAttrs: false,
        model: {
            prop: 'modelValue',
            event: 'input',
        },
        props: {
            checked: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, sets the `checked` attribute.',
                    support: ['v-model'],
                },
            },
            disabled: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, sets the `disabled` attribute.',
                },
            },
            label: {
                type: String,
                docs: {
                    validation: '**Required**',
                    description: 'Sets the radio button label. Can also be passed via the [default slot](#slots).',
                },
            },
            microcopy: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the radio button microcopy.',
                    support: ['v-html'],
                },
            },
            modelValue: {
                default: undefined,
                internal: true,
                docs: {
                    validation: '—',
                    description: 'Only for internal use by the component, do not manually adjust this prop.',
                },
            },
            name: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the `name` attribute.',
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large', 'touch'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`, `touch`.',
                    description: 'Sets the size of the radio button.',
                },
            },
            value: {
                docs: {
                    validation: '—',
                    description: 'Sets the `value` attribute.',
                },
            },
            version: {
                type: String,
                default: `${name}-${version}`,
                docs: {
                    validation: 'Read-only',
                    description:
                        'Set automatically by package version, do not manually adjust this prop. Used to add a data attribute providing the component version to the component markup rendered in the DOM.',
                },
            },
        },
        computed: {
            sizeClass() {
                return this.$style[`mds-radio-button--${this.size}`];
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-component--on-dark']] = this.onDark;

                return obj;
            },
            isChecked() {
                return this.modelValue === undefined ? this.checked : this.modelValue === this.value;
            },
        },
        methods: {
            toggleCheck(event) {
                this.$emit('input', this.isChecked ? '' : this.value, event);
            },
        },
        watch: {
            checked(newValue) {
                if (newValue !== this.isChecked) {
                    this.toggleCheck();
                }
            },
        },
    };
</script>

<style lang="scss" module>
    @import './radio-button.scss';
</style>
